/**
 * The editable shape of a reading, and the two payloads it produces.
 *
 * This lives apart from the form component because create and edit have
 * *different* rules for an empty box, and getting them the wrong way round
 * loses data silently:
 *
 * - POST /api/readings — a blank box is simply not sent. There is nothing to
 *   erase yet, and sending `null` would be indistinguishable from not testing.
 * - PUT /api/readings/{id} — the handler applies `exclude_unset`, so an omitted
 *   key leaves the column *as it was*. A box the user cleared therefore has to
 *   be sent as an explicit `null`, or blanking a measurement appears to work
 *   and quietly changes nothing.
 *
 * Both rules preserve the same thing: NULL means "not tested", never zero.
 */

import { toLocalInputValue } from "@/lib/hooks";
import { PARAMS, type ParamKey } from "@/lib/params";
import type { Clarity, EntryMode, Reading } from "@/types";

/** Raw input strings, not numbers — "" is a meaningful state and 0 is not it. */
export type ParamInputs = Partial<Record<ParamKey, string>>;

export interface ReadingDraft {
  mode: EntryMode;
  values: ParamInputs;
  /** A `datetime-local` string in the browser's zone, not an ISO instant. */
  takenAt: string;
  clarity: Clarity | "";
  notes: string;
  /**
   * "The pH sample was neutralised with R-0007 before testing."
   *
   * A checkbox has two states but the column has three, so the mapping is
   * deliberately lopsided: ticked sends `true`, un-ticked sends `null` — "not
   * recorded" — and never `false`. An un-ticked box cannot tell "I did not
   * neutralise" from "I did not read the question", and the server treats the
   * two identically anyway (both block a pH dose). Recording the stronger claim
   * from the weaker evidence would put provenance on the reading that nobody
   * asserted.
   */
  phNeutralised: boolean;
}

const ALL_PARAMS = Object.keys(PARAMS) as ParamKey[];

/**
 * The instant a `datetime-local` box is pointing at, or null if it isn't
 * pointing at one yet.
 *
 * An emptied or half-entered box reads back as a string `Date` cannot parse,
 * and `toISOString()` on the result *throws*. The edit form diffs its draft on
 * every keystroke, so an unguarded conversion takes the whole screen down
 * mid-edit. ReadingForm is what stops such a draft being submitted; this keeps
 * the diff itself total.
 */
export function takenAtIso(local: string): string | null {
  const at = new Date(local);
  return Number.isNaN(at.getTime()) ? null : at.toISOString();
}

export function blankDraft(now: Date = new Date()): ReadingDraft {
  return {
    mode: "quick",
    values: {},
    takenAt: toLocalInputValue(now),
    clarity: "",
    notes: "",
    phNeutralised: false,
  };
}

/**
 * Seeds the edit form from a saved reading.
 *
 * Measurements keep the server's own string form ("4.0", not 4) so that a field
 * nobody touched compares equal to its draft and stays out of the update
 * entirely.
 */
export function draftFromReading(reading: Reading): ReadingDraft {
  const values: ParamInputs = {};
  for (const key of ALL_PARAMS) {
    const raw = reading[key];
    values[key] = raw === null || raw === undefined ? "" : String(raw);
  }

  return {
    mode: reading.entry_mode,
    values,
    takenAt: toLocalInputValue(new Date(reading.taken_at)),
    clarity: reading.clarity ?? "",
    notes: reading.notes ?? "",
    // Only an explicit true ticks the box. Both null ("not recorded") and false
    // ("recorded as not neutralised") come back un-ticked, which is honest: the
    // checkbox has no way to show the difference, and leaving it un-ticked
    // means an edit that touches nothing else sends nothing for this field.
    phNeutralised: reading.ph_chlorine_neutralised === true,
  };
}

/**
 * A reading with no measurement, clarity or note carries no information.
 *
 * Counts only `shown` params, because that is exactly what both payload
 * builders send. A value typed under "Full panel" and then hidden by switching
 * back to "Quick check" stays in the draft but leaves the request, so counting
 * it here would keep Save enabled for a payload that carries no measurement at
 * all — the server rejects that, and the user is told nothing about the CYA
 * they believe they just entered.
 */
export function draftHasContent(draft: ReadingDraft, shown: readonly ParamKey[]): boolean {
  // `phNeutralised` is deliberately absent. It qualifies the pH rather than
  // standing beside it, so a reading carrying nothing but a ticked box records
  // no water condition at all — the server rejects exactly that, and counting
  // it here would keep Save enabled for a request that comes back 422.
  return (
    shown.some((key) => (draft.values[key] ?? "").trim() !== "") ||
    draft.clarity !== "" ||
    draft.notes.trim() !== ""
  );
}

export function buildCreatePayload(
  draft: ReadingDraft,
  shown: readonly ParamKey[],
): Record<string, unknown> {
  const payload: Record<string, unknown> = { entry_mode: draft.mode };

  const takenAt = takenAtIso(draft.takenAt);
  if (takenAt) payload.taken_at = takenAt;

  // Send only what was actually measured. An omitted field is a real,
  // meaningful "not tested" — never coerce it to zero.
  for (const key of shown) {
    const raw = (draft.values[key] ?? "").trim();
    if (raw !== "") payload[key] = raw;
  }
  if (draft.clarity) payload.clarity = draft.clarity;
  if (draft.notes.trim()) payload.notes = draft.notes.trim();
  // Same rule as a blank measurement box: say it only when it was said. An
  // un-ticked box is "not recorded", which is the column's default, so sending
  // `false` would upgrade silence into a claim.
  if (draft.phNeutralised) payload.ph_chlorine_neutralised = true;

  return payload;
}

/**
 * Diff of a draft against the reading it was seeded from.
 *
 * Only `shown` params are considered, which is safe because the form forces any
 * param with a stored value to stay visible — a value that cannot be seen can
 * neither be edited nor accidentally dropped here.
 */
export function buildUpdatePayload(
  initial: ReadingDraft,
  draft: ReadingDraft,
  shown: readonly ParamKey[],
): Record<string, unknown> {
  const payload: Record<string, unknown> = {};

  for (const key of shown) {
    const before = (initial.values[key] ?? "").trim();
    const after = (draft.values[key] ?? "").trim();
    if (after === before) continue;
    // Cleared, so say so out loud. Omitting it would leave the old value.
    payload[key] = after === "" ? null : after;
  }

  if (draft.clarity !== initial.clarity) payload.clarity = draft.clarity || null;

  // Un-ticking has to reach the server as an explicit null, for the same reason
  // a cleared measurement does: the handler applies `exclude_unset`, so omitting
  // the key leaves the column true and the pH goes on being trusted at a
  // chlorine level that bleaches the dye. Silently ignoring a correction here is
  // worse than the original lockout — the user would have said so and been
  // overruled without being told.
  if (draft.phNeutralised !== initial.phNeutralised) {
    payload.ph_chlorine_neutralised = draft.phNeutralised ? true : null;
  }

  const notes = draft.notes.trim();
  if (notes !== initial.notes.trim()) payload.notes = notes || null;

  if (draft.mode !== initial.mode) payload.entry_mode = draft.mode;

  // `datetime-local` has minute resolution, so re-sending an untouched
  // timestamp would shave the seconds off the stored instant. Omitting it
  // leaves the instant exactly as recorded.
  if (draft.takenAt !== initial.takenAt) {
    const takenAt = takenAtIso(draft.takenAt);
    if (takenAt) payload.taken_at = takenAt;
  }

  return payload;
}
